import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";
import { ArrowRight } from "lucide-react";

const programs = [
  {
    slug: "personal-training",
    name: "Personal Training",
    description:
      "Experience the power of one-on-one coaching with certified trainers who understand your unique goals, limitations, and lifestyle. Whether you prefer training at the gym, in parks, or in the comfort of your home, our trainers adapt their approach to fit your needs. Get personalized workout plans, real-time form correction, motivation when you need it most, and accountability that actually works.",
    outcomes: ["Certified expert trainers", "Custom workout programming", "Form correction & safety", "Flexible scheduling", "Real-time feedback", "Long-term accountability"],
    image: "/hero/hero_personal_training.png",
    features: ["1-on-1 sessions", "Progress tracking", "Nutrition integration", "Goal setting"],
  },
  {
    slug: "nutrition",
    name: "Nutrition & Meal Planning",
    description:
      "Work with registered nutritionists who create evidence-based meal plans tailored to your body, goals, and lifestyle. Our nutrition programs go beyond calorie counting—we teach you sustainable eating habits, provide macro and micronutrient guidance, offer recipe suggestions, and help you build a healthy relationship with food that lasts a lifetime.",
    outcomes: ["Personalized meal plans", "Macro & micronutrient guidance", "Sustainable habit building", "Recipe library", "Weekly check-ins", "Dietary restriction support"],
    image: "/hero/hero_nutrition.png",
    features: ["Custom meal plans", "Grocery lists", "Recipe suggestions", "Progress monitoring"],
  },
  {
    slug: "park-classes",
    name: "Park & Group Classes",
    description:
      "Experience the energy of outdoor group fitness in carefully curated parks across your city. Join bootcamp sessions, yoga flows, HIIT workouts, and mobility classes led by expert instructors. Enjoy the benefits of fresh air, natural sunlight, and a supportive community while achieving your fitness goals. All classes include QR check-in for seamless attendance tracking.",
    outcomes: ["Expert-led classes", "Community motivation", "Fresh air & nature", "Structured workouts", "Flexible attendance", "Social connection"],
    image: "/hero/hero_home_workout.png",
    features: ["Multiple class types", "QR check-in", "Class reviews", "Flexible booking"],
  },
  {
    slug: "online-coaching",
    name: "Online Coaching",
    description:
      "Train from anywhere in the world with our comprehensive online coaching program. Get live virtual training sessions, pre-recorded workout libraries, video form check-ins, chat support with your coach, and detailed progress tracking—all accessible from your phone, tablet, or computer. Perfect for busy professionals, travelers, or anyone who prefers the flexibility of remote training.",
    outcomes: ["Train from anywhere", "Flexible scheduling", "Remote accountability", "Video check-ins", "Workout library access", "24/7 chat support"],
    image: "/hero/hero_yoga.png",
    features: ["Live sessions", "On-demand workouts", "Video feedback", "Progress tracking"],
  },
];

export default function ProgramsPage() {
  const breadcrumbData = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://fiteasy.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Programs",
        item: "https://fiteasy.com/programs",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Programs"
        description="Explore FitEasy programs including personal training, nutrition coaching, park classes, and online coaching. Find the perfect fitness program to match your goals."
        path="/programs"
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

        <main id="main-content" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <SectionHeader
            id="programs-heading"
            label="Programs"
            title="Comprehensive programs designed for real results"
            description="FitEasy offers a complete ecosystem of fitness solutions—from one-on-one personal training to group park classes, from personalized nutrition coaching to flexible online programs. Each program is designed by certified professionals and tailored to help you achieve sustainable, long-term results. Whether you're just starting out or looking to take your fitness to the next level, we have the right program for you."
            className="mb-12"
          />

          <section aria-labelledby="programs-heading" className="grid gap-8 md:grid-cols-2">
            {programs.map((program) => (
              <article
                key={program.slug}
                className="flex flex-col overflow-hidden rounded-3xl border border-blue-100 bg-white/90 shadow-[0_18px_60px_rgba(59,130,246,0.08)] transition hover:shadow-[0_18px_60px_rgba(59,130,246,0.15)] focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2"
              >
                <div className="relative h-56 w-full overflow-hidden">
                  <img
                    src={program.image}
                    alt={`${program.name} - ${program.description}`}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-4 p-6">
                  <div>
                    <h2 className="font-display text-xl text-gray-900">
                      {program.name}
                    </h2>
                    <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                      {program.description}
                    </p>
                  </div>
                  <div className="mt-auto space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                      You&apos;ll achieve
                    </p>
                    <ul className="flex flex-wrap gap-2 text-xs text-gray-700" role="list">
                      {program.outcomes.map((item) => (
                        <li
                          key={item}
                          role="listitem"
                          className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                    {program.features && (
                      <div className="mt-3 pt-3 border-t border-blue-100">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600 mb-2">
                          What&apos;s included
                        </p>
                        <ul className="space-y-1 text-xs text-gray-600" role="list">
                          {program.features.map((feature) => (
                            <li key={feature} role="listitem" className="flex items-center gap-2">
                              <span className="text-blue-600 font-bold">•</span>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <CTAButton
                      href={`/${program.slug}`}
                      variant="primary"
                      className="mt-4 w-full justify-center text-sm"
                      ariaLabel={`Learn more about ${program.name}`}
                    >
                      Learn More
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </CTAButton>
                  </div>
                </div>
              </article>
            ))}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

